/**
 * Sickness-reduction meters — the 5 daily levers that AAP + WHO evidence
 * links to lower infant illness rates:
 *   1. Vitamin D (400 IU/day for breastfed)
 *   2. Tummy time (age-scaled target)
 *   3. Outdoor time (target 30 min daylight)
 *   4. Skin-to-skin contact (higher target for younger)
 *   5. Sleep adequacy (24h total vs age target)
 *
 * Each meter is 0-1.0 progress toward today's target. Not a score, not
 * a game — a diagnostic. Full ring = you nailed the target; partial ring
 * shows what's left.
 */

import type { AgeBucket } from './blocks'

export interface Meter {
  kind: 'vitamin_d' | 'tummy_time' | 'outdoor' | 'skin_to_skin' | 'sleep_adequacy'
  label: string
  actual_min: number
  target_min: number
  progress: number  // 0..1
  status: 'not-started' | 'progressing' | 'target-met'
  why_short: string
  source_slug?: string
}

export interface MeterInputs {
  bucket: AgeBucket | null
  completionsToday: Array<{ block_type: string; minutes_actual: number | null; completed_at: string }>
  sleepHoursLast24h: number
}

export function computeMeters(inputs: MeterInputs): Meter[] {
  if (!inputs.bucket) return []

  const targets = TARGETS[inputs.bucket]
  const totalsMin: Record<string, number> = {
    tummy_time: 0, outdoor: 0, skin_to_skin: 0,
  }
  let vitDGiven = false
  for (const c of inputs.completionsToday) {
    const m = c.minutes_actual ?? 0
    if (c.block_type === 'tummy-time')    totalsMin.tummy_time += m
    if (c.block_type === 'outdoor')       totalsMin.outdoor += m
    if (c.block_type === 'skin-to-skin')  totalsMin.skin_to_skin += m
    if (c.block_type === 'vitamin-d')     vitDGiven = true
  }

  const sleepH = inputs.sleepHoursLast24h
  const sleepPct = Math.min(1, sleepH / targets.sleep_target_h)

  return [
    {
      kind: 'vitamin_d',
      label: 'Vitamin D',
      actual_min: vitDGiven ? 1 : 0,
      target_min: 1,
      progress: vitDGiven ? 1 : 0,
      status: vitDGiven ? 'target-met' : 'not-started',
      why_short: '400 IU/day for exclusively breastfed infants (AAP).',
      source_slug: 'aap-outdoor-vitd',
    },
    {
      kind: 'tummy_time',
      label: 'Tummy time',
      actual_min: totalsMin.tummy_time,
      target_min: targets.tummy_min,
      progress: Math.min(1, totalsMin.tummy_time / targets.tummy_min),
      status: statusFor(totalsMin.tummy_time, targets.tummy_min),
      why_short: 'Prevents flat head, builds neck + core strength for rolling and sitting.',
      source_slug: 'aap-tummy-time',
    },
    {
      kind: 'outdoor',
      label: 'Outdoor',
      actual_min: totalsMin.outdoor,
      target_min: targets.outdoor_min,
      progress: Math.min(1, totalsMin.outdoor / targets.outdoor_min),
      status: statusFor(totalsMin.outdoor, targets.outdoor_min),
      why_short: 'Daylight regulates circadian rhythm; fresh air supports respiratory health.',
      source_slug: 'aap-outdoor-vitd',
    },
    {
      kind: 'skin_to_skin',
      label: 'Skin-to-skin',
      actual_min: totalsMin.skin_to_skin,
      target_min: targets.skin_min,
      progress: Math.min(1, totalsMin.skin_to_skin / targets.skin_min),
      status: statusFor(totalsMin.skin_to_skin, targets.skin_min),
      why_short: 'Regulates temp + heart rate; boosts breastfeeding success + bonding.',
      source_slug: 'aap-feeding-newborn',
    },
    {
      kind: 'sleep_adequacy',
      label: 'Sleep (24h)',
      actual_min: Math.round(sleepH * 60),
      target_min: targets.sleep_target_h * 60,
      progress: sleepPct,
      status: sleepPct >= 0.9 ? 'target-met' : sleepPct > 0 ? 'progressing' : 'not-started',
      why_short: `Age-typical target is ~${targets.sleep_target_h}h/24h. Adequate sleep is the strongest single immune-support factor.`,
      source_slug: 'aap-safe-sleep-2022',
    },
  ]
}

interface TargetSet {
  tummy_min: number
  outdoor_min: number
  skin_min: number
  sleep_target_h: number
}

const TARGETS: Record<AgeBucket, TargetSet> = {
  '0-3mo':  { tummy_min: 15, outdoor_min: 20, skin_min: 60, sleep_target_h: 15 },
  '3-6mo':  { tummy_min: 30, outdoor_min: 30, skin_min: 30, sleep_target_h: 14 },
  '6-9mo':  { tummy_min: 30, outdoor_min: 45, skin_min: 15, sleep_target_h: 13 },
  '9-12mo': { tummy_min: 20, outdoor_min: 60, skin_min: 10, sleep_target_h: 13 },
}

function statusFor(actual: number, target: number): Meter['status'] {
  if (actual <= 0) return 'not-started'
  if (actual >= target) return 'target-met'
  return 'progressing'
}
